import { createContext, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router';
import qs from 'query-string';

import { FCWithChildren } from 'common/types';

type Filters = { q: string };

const defaults: Filters = {
  q: '',
};

interface ContextType {
  filters: Filters;
  updateFilters: (newFilters: Filters) => void;
}

export const FiltersContext = createContext<ContextType>({
  filters: defaults,
  updateFilters: () => {},
});

const searchToFilters = (search: string): Filters => {
  const filters = qs.parse(search);

  return { q: filters.q?.toString() ?? '' };
};

export const FiltersProvider: FCWithChildren = ({ children }) => {
  const history = useHistory();

  const filters = useMemo(
    () => searchToFilters(history.location.search),
    [history.location.search],
  );

  const updateFilters = useCallback(
    (newFilters: Filters) => {
      history.push({
        search: qs.stringify(
          { ...newFilters },
          {
            skipEmptyString: true,
            skipNull: true,
          },
        ),
      });
    },
    [history],
  );

  return (
    <FiltersContext.Provider
      value={{
        filters,
        updateFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export function useFilters() {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error('useFilters can only be used inside of a FiltersProvider');
  }

  return context;
}
